<template>
  <div class="bg-white">
    <FlatsFilter v-model="flatsFilter" :show-stage-and-date="showStageAndDate" :show-date="showDate" :get-stage="stages" :show-entrance="showEntrance" />
    <div class="compare-button" v-if="selectedItems.length > 0" @click="scrollToCompareTable">
      <div class="d-flex align-items-center">
        <div class="mr-4">
          <img src="../../assets/icons/compare.svg"> 
        </div>
        <div class="mr-4">
          <p class="mb-1">{{ $t('Porovnávanie bytov') }}</p>
          <p class="small-text mb-0">{{ $t('Pridané byty:') }} {{selectedItems.length}}</p>
        </div>
        <div>
          <a @click="scrollToCompareTable" class="more">{{ $t('Zobraziť') }}</a>
        </div>
      </div>
      <div class="compare-flats-detail border-top w-100 mt-1 pt-1 d-none d-md-block">
        <div v-for="(flat, id) in compare" :key="id" class="row align-items-center">
          <div class="col">{{ flat.flat_number_of_rooms }} {{ $t('izb.') }}</div>
          <div class="col">{{ flat.flat_penta_internal_id }}</div>
          <div class="col" v-if="isPriceOnRequest(flat)">Cena na vyžiadanie</div>
          <div v-else class="col">{{currency(flat.flat_price)}} €</div>
          <div class="col-2 d-flex justify-content-end"><div class="d-flex align-items-center p-2" @click="deleteFromCompare(flat, false)"><img class="delete-item" width="10" src="../../assets/icons/close.svg" alt=""></div></div>
        </div>
      </div>
    </div>
    <div class="flats-table">
      <div v-if="showIconLegend && legendHints.length" class="price-hint-legend d-flex flex-wrap mb-3">
        <span v-for="hint in legendHints" :key="hint.icon" class="d-inline-flex align-items-center mr-4">
          <span class="icon"><BenefitIcon :name="hint.icon" :stroke="1.1" /></span>
          {{ $t(hint.label) }}
        </span>
      </div>
      <b-table
          selectable
          responsive
          tabindex="row.index"
          @row-clicked="onRowSelected"
          select-mode="single"
          :items="filteredFlats(flatsFilter)"
          :fields="fields"
          :per-page="perPage"
          :filter="filter"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          ref="flatsTable"
          :tbody-tr-class="isDisabled"
      >
        <template #cell(selected)="{ rowSelected }">
          <template v-if="rowSelected">
            <span aria-hidden="true">&check;</span>
            <span class="sr-only">Selected</span>
          </template>
        </template>
        <template #cell(building.building_stage)="data">
          <div v-if="data.value === 'bory3'"><img width="40" src="../../assets/images/gallery-slides/bory3_small.png" alt="Bory 3"></div>
          <div v-if="data.value === 'bory2'"><img width="40" src="../../assets/images/gallery-slides/bory2_small.png" alt="Bory 2"></div>
          <div v-if="data.value === 'bory2b'"><img width="40" src="../../assets/images/gallery-slides/bory2_small.png" alt="Bory 2"></div>
          <div v-if="data.value === 'bory4'"><img width="40" src="../../assets/images/na-hradzi.png" alt="Na Hrádzi"></div>
          <div v-if="data.value === 'bory5'"><img width="40" src="../../assets/images/nadvorie_logo.png" alt="Nádvorie"></div>
          <div v-if="data.value === 'boryNoveBloky'"><img width="40" src="../../assets/images/bory-nove-bloky-logo.png" alt="Nádvorie"></div>
        </template>
        <template #cell(url_link_to_the_vr_apartments_tour)="data">
          <a v-if="data.value" :href="data.value" target="_blank" class="vr-icon"><img width="34" height="20" src="../../assets/icons/360.svg" alt=""><br>{{ $t('Spustiť') }}</a>
        </template>
        <template #cell(flat_price_before_discount_vat)="data">
          <div class="d-flex align-items-center">
            <span class="text-nowrap">{{ data.value }}</span>
            <template v-if="data.value && !isPriceOnRequest(data.item)">
              <span v-for="hint in (priceHints[data.item.building.building_stage] || [])" :key="hint.icon" class="price-hint ml-2">
                <span class="icon"><BenefitIcon :name="hint.icon" :stroke="1.1" /></span>
                <span class="price-hint-tooltip">{{ $t(hint.label) }}</span>
              </span>
            </template>
          </div>
        </template>
        <template #cell(flat_area)="data">
          {{data.value}} m<sup>2</sup>
        </template>
        <template #cell(flat_area_total)="data">
          {{data.value}} m<sup>2</sup>
        </template>
        <template #cell(flat_type)="data">
          <div class="d-flex align-items-center justify-content-center">
            <div class="d-flex align-items-center">
              <div v-if="data.value === '1' && data.item.building.building_name !== 'B2'">{{ $t('Byt') }}</div>
              <div v-if="data.value === '19'">{{ $t('Apartmán') }}</div>
              <div v-if="data.item.building.building_name == 'B2'">Družstevné bývanie</div>
              <div class="ml-1 premium-standard" v-if="data.item.building.building_name === 'B2'">
                <div class="icon">
                  <img src="../../assets/images/druzstevne/druzstevne_byvanie.svg">
                </div>
              </div>
              <div class="ml-1 premium-standard" v-else-if="data.item.building.building_stage === 'bory4' && (data.item.flat_number_of_rooms == '3' || data.item.flat_number_of_rooms == '4')">
                <div class="popup-info">
                  <div class="text">
                    {{ $t('Vyšší štandard v cene bytu') }}
                  </div>
                </div>
                <div class="icon">
                  <img src="../../assets/icons/premium.png">
                </div>
              </div>
            </div>
          </div>

        </template>
        <template #cell(flat_area_other)="data">
          {{data.value}} m<sup>2</sup>
        </template>
        <template #cell(flat_status)="data">
          <div v-if="data.item.building.building_stage == 'bory4'">-</div>
          <div v-if="data.value === '0' && data.item.building.building_stage == 'bory5'" class="text-success font-weight-bold">{{ $t('Dostupné') }}</div>
          <div v-if="data.value === '0' && data.item.building.building_stage !== 'bory5' && data.item.building.building_stage !== 'bory4'" class="text-success font-weight-bold">{{ $t('Voľný') }}</div>
          <div v-if="data.value === '1'">{{ $t('Predrezervované') }}</div>
          <div v-if="data.value === '2'">{{ $t('Rezervované') }}</div>
          <div v-if="data.value === '3'">{{ $t('Predané') }}</div>
          <div v-if="data.value === '4'">{{ $t('Kontaktujte predajcu') }}</div>
          <div v-if="data.value === '5'">{{ $t('Kontaktujte predajcu') }}</div>
        </template>
        <template #cell(flat_monthly_payment)="data">
          <div v-if="data.item.flat_monthly_payment" class="d-flex align-items-center">
            <span>{{ formatMonthlyPayment(data.item.flat_monthly_payment) }}</span>
            <div class="ml-1 payment-info-wrap">
              <div class="payment-tooltip">
                <div class="text">Indikatívna výška mesačnej splátky úveru na byt v rámci družstva je vypočítaná pri dobe splatnosti 40 rokov a výške úroku 4,5&nbsp;% p.&nbsp;a. Splátka nezahŕňa poplatok za odborné vedenie družstva, poplatok za správu bytového domu, mesačné zálohové platby a ďalšie individuálne náklady ako je napríklad poistenie alebo internet.</div>
              </div>
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="info-icon"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>
            </div>
          </div>
          <div v-else-if="data.item.building.building_stage !== 'bory4'">-</div>
        </template>
        <template #cell(porovnat)="data">
          <b-form-checkbox
              v-if="data.item.flat_status === '0' || data.item.flat_status === '1'"
              @change="addToCompare($event, data)"
              v-model="selectedItems"
              :disabled="selectedItems.length >= 4 && selectedItems.indexOf(data.index) === -1"
              :value="data.item.flat_penta_internal_id"
              unchecked-value="not_accepted"
          ></b-form-checkbox>
        </template>
      </b-table>
      <p class="text-center text-dark m-5" v-if="filteredFlats(flatsFilter).length === 0">{{ $t('Vybraným kritériám nezodpovedá žiadny byt.') }}</p>
      <div class="d-flex align-items-center justify-content-center">
        <b-button v-if="perPage <= filteredFlats(flatsFilter).length" class="btn btn-primary rounded-0" @click="loadMoreFlats">{{ $t('Načítať ďalšie') }}</b-button>
      </div>
    </div>
    <div v-if="compare.length > 0" class="flats-table compare-table" id="compare-table">
      <h3 class="text-dark">{{ $t('Vybrané byty - porovnanie') }}</h3>
      <b-table
          selectable
          responsive
          @row-clicked="onRowSelected"
          select-mode="single"
          :items="compare"
          :fields="fields"
      >
        <template #cell(selected)="{ rowSelected }">
          <template v-if="rowSelected">
            <span aria-hidden="true">&check;</span>
            <span class="sr-only">Selected</span>
          </template>
        </template>
        <template #cell(building.building_stage)="data">
          <div v-if="data.value === 'bory3'"><img width="40" src="../../assets/images/gallery-slides/bory3_small.png" alt="Bory 3"></div>
          <div v-if="data.value === 'bory2'"><img width="40" src="../../assets/images/gallery-slides/bory2_small.png" alt="Bory 2"></div>
          <div v-if="data.value === 'bory2b'"><img width="40" src="../../assets/images/gallery-slides/bory2_small.png" alt="Bory 2"></div>
          <div v-if="data.value === 'bory4'"><img width="40" src="../../assets/images/na-hradzi.png" alt="Na Hrádzi"></div>
          <div v-if="data.value === 'boryNoveBloky'"><img width="40" src="../../assets/images/bory-nove-bloky-logo.png" alt="Nádvorie"></div>
        </template>
        <template #cell(flat_area)="data">
          {{data.value}} m<sup>2</sup>
        </template>
        <template #cell(flat_area_total)="data">
          {{data.value}} m<sup>2</sup>
        </template>
        <template #cell(flat_type)="data">
          <div class="d-flex align-items-center">
            <div v-if="data.value === '1'">{{ $t('Byt') }}</div>
            <div v-if="data.value === '19'">{{ $t('Apartmán') }}</div>
            <div class="ml-1 premium-standard" v-if="(data.item.building.building_stage === 'bory2b' || data.item.building.building_stage === 'bory4') && (data.item.flat_number_of_rooms == '3' || data.item.flat_number_of_rooms == '4')">
              <div class="popup-info">
                <div class="text">
                  {{ $t('Vyšší štandard v cene bytu') }}
                </div>
              </div>
              <div class="icon">
                <img src="../../assets/icons/premium.png">
              </div>
            </div>
          </div>
        </template>
        <template #cell(flat_area_other)="data">
          {{data.value}} m<sup>2</sup>
        </template>
        <template #cell(flat_status)="data">
          <div v-if="data.value === '0'" class="text-success font-weight-bold">{{ $t('Voľný') }}</div>
          <div v-if="data.value === '1'">{{ $t('Predrezervované') }}</div>
          <div v-if="data.value === '2'">{{ $t('Rezervované') }}</div>
          <div v-if="data.value === '3'">{{ $t('Predané') }}</div>
        </template>
        <template #cell(flat_price)="data">
          <span>{{data.value}}</span>
        </template>
        <template #cell(porovnat)="data">
          <b-link class="p-2" @click="deleteFromCompare(data.item, false)"><img width="14" src="../../assets/icons/close.svg" alt=""></b-link>
        </template>
        <template #cell(flat_monthly_payment)="data">
          <div v-if="data.item.flat_monthly_payment" class="d-flex align-items-center">
            <span>{{ formatMonthlyPayment(data.item.flat_monthly_payment) }}</span>
            <div class="ml-1 payment-info-wrap">
              <div class="payment-tooltip">
                <div class="text">Indikatívna výška mesačnej splátky úveru na byt v rámci družstva je vypočítaná pri dobe splatnosti 40 rokov a výške úroku 4,5&nbsp;% p.&nbsp;a. Splátka nezahŕňa poplatok za odborné vedenie družstva, poplatok za správu bytového domu, mesačné zálohové platby a ďalšie individuálne náklady ako je napríklad poistenie alebo internet.</div>
              </div>
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="info-icon"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>
            </div>
          </div>
          <div v-else-if="data.item.building.building_stage !== 'bory4'">-</div>
        </template>
        <template #cell(url_link_to_the_vr_apartments_tour)="data">
          <a v-if="data.value" :href="data.value" target="_blank" class="vr-icon"><img width="34" height="20" src="../../assets/icons/360.svg" alt=""><br>{{ $t('Spustiť') }}</a>
        </template>
      </b-table>
      <div class="d-flex justify-content-center">
        <b-button class="btn btn-primary rounded-0" @click="showCompareModal">{{ $t('Zobraziť porovnanie') }}</b-button>
      </div>
      <b-modal id="flatsCompareModal" size="xl" title="" centered hide-footer>
        <div class="d-flex flex-wrap">
          <b-col v-for="(flat, index) in popupFlats" :key="index" class="d-flex justify-content-between flex-column col-12 col-md mb-4 mb-md-0">
            <b-link class="remove-from-compare" @click="deleteFromCompare(flat.data.data, true)"><img width="14" src="../../assets/icons/close.svg" alt=""></b-link>
            <div>
              <h3 class="text-center" v-if="isOneAndHalfRoom(flat.data.data)"> {{ $t('Jednoizbový byt') }} {{flat.data.data.flat_penta_internal_id}}</h3>
              <h3 class="text-center" v-else-if="roomsTitle(flat.data.data)"> {{ $t(roomsTitle(flat.data.data)) }} {{flat.data.data.flat_penta_internal_id}}</h3>
              <h3 class="text-center" v-else-if="flat.data.data.flat_penta_internal_id">{{flat.data.data.flat_penta_internal_id}}</h3>
              <img :src="flat.data.data.flat_plan" alt="">
              <div class="data">
                <div class="d-flex justify-content-between bg border-bottom">
                  <div class="label">{{ $t('Byt') }}</div>
                  <div class="value">{{flat.data.data.flat_penta_internal_id}}</div>
                </div>
                <div class="d-flex justify-content-between border-bottom">
                  <div class="label">{{ $t('ID bytu') }}</div>
                  <div class="value">{{flat.data.data.flat_internal_id}}</div>
                </div>
                <div class="d-flex justify-content-between border-bottom">
                  <div class="label">{{ $t('Blok') }}</div>
                  <div class="value">{{flat.data.data.building.building_name}}</div>
                </div>
                <div class="d-flex justify-content-between border-bottom">
                  <div class="label">{{ $t('Podlažie') }}</div>
                  <div class="value">{{flat.data.data.building.floor_number}}</div>
                </div>
                <div class="d-flex justify-content-between border-bottom mb-3">
                  <div class="label">{{ $t('Počet izieb') }}</div>
                  <div class="value">{{flat.data.data.flat_number_of_rooms}}</div>
                </div>

                <div class="d-flex justify-content-between pl-5">
                  <div class="label">{{ $t('Kúpeľňa') }}</div>
                  <div class="value">{{flat.data.data.flat_area_bathroom1}} m<sup>2</sup></div>
                </div>
                <div v-if="flat.data.data.flat_area_bathroom2" class="d-flex justify-content-between pl-5">
                  <div class="label">{{ $t('Kúpeľňa 2') }}</div>
                  <div class="value">{{flat.data.data.flat_area_bathroom2}} m<sup>2</sup></div>
                </div>
                <div v-if="flat.data.data.flat_area_balcony" class="d-flex justify-content-between pl-5">
                  <div class="label">{{ $t('Balkón') }}</div>
                  <div class="value">{{flat.data.data.flat_area_balcony}} m<sup>2</sup></div>
                </div>
                <div v-if="flat.data.data.flat_area_bedroom" class="d-flex justify-content-between pl-5">
                  <div class="label">{{ $t('Spálňa') }}</div>
                  <div class="value">{{flat.data.data.flat_area_bedroom}} m<sup>2</sup></div>
                </div>
                <div v-if="flat.data.data.flat_area_corridor1" class="d-flex justify-content-between pl-5">
                  <div class="label">{{ $t('Chodba') }}</div>
                  <div class="value">{{flat.data.data.flat_area_corridor1}} m<sup>2</sup></div>
                </div>
                <div v-if="flat.data.data.flat_area_corridor2" class="d-flex justify-content-between pl-5">
                  <div class="label">{{ $t('Chodba 2') }}</div>
                  <div class="value">{{flat.data.data.flat_area_corridor2}} m<sup>2</sup></div>
                </div>
                <div v-if="flat.data.data.flat_area_hall" class="d-flex justify-content-between pl-5">
                  <div class="label">{{ $t('Chodba') }}</div>
                  <div class="value">{{flat.data.data.flat_area_hall}} m<sup>2</sup></div>
                </div>
                <div v-if="flat.data.data.flat_area_kitchen" class="d-flex justify-content-between pl-5">
                  <div class="label">{{ $t('Kuchyňa') }}</div>
                  <div class="value">{{flat.data.data.flat_area_kitchen}} m<sup>2</sup></div>
                </div>
                <div v-if="flat.data.data.flat_area_livingroom_kitchen" class="d-flex justify-content-between pl-5">
                  <div class="label">{{ $t('Obývacia izba + Kuchyňa') }}</div>
                  <div class="value">{{flat.data.data.flat_area_livingroom_kitchen}} m<sup>2</sup></div>
                </div>
                <div v-if="flat.data.data.flat_area_loggia" class="d-flex justify-content-between pl-5">
                  <div class="label">{{ $t('Loggia') }}</div>
                  <div class="value">{{flat.data.data.flat_area_loggia}} m<sup>2</sup></div>
                </div>
                <div v-if="flat.data.data.flat_area_loggia2" class="d-flex justify-content-between pl-5">
                  <div class="label">{{ $t('Loggia 2') }}</div>
                  <div class="value">{{flat.data.data.flat_area_loggia2}} m<sup>2</sup></div>
                </div>
                <!-- Zelenú plochu posiela Realpad raz ako flat_area_garden, raz ako
                     flat_area_front_garden, v B2 (družstvo) v oboch kľúčoch naraz.
                     Klient chce jediný riadok "Záhrada" – preto fallback, nie súčet. -->
                <div v-if="flat.data.data.flat_area_garden || flat.data.data.flat_area_front_garden" class="d-flex justify-content-between pl-5">
                  <div class="label">{{ $t('Záhrada') }}</div>
                  <div class="value">{{flat.data.data.flat_area_garden || flat.data.data.flat_area_front_garden}} m<sup>2</sup></div>
                </div>
                <div v-if="flat.data.data.flat_area_room1" class="d-flex justify-content-between pl-5">
                  <div class="label">{{ $t('Izba') }}</div>
                  <div class="value">{{flat.data.data.flat_area_room1}} m<sup>2</sup></div>
                </div>
                <div v-if="flat.data.data.flat_area_room2" class="d-flex justify-content-between pl-5">
                  <div class="label">{{ $t('Izba') }} 2</div>
                  <div class="value">{{flat.data.data.flat_area_room2}} m<sup>2</sup></div>
                </div>
                <div v-if="flat.data.data.flat_area_sleeping_niche" class="d-flex justify-content-between pl-5">
                  <div class="label">{{ $t('Spací výklenok') }}</div>
                  <div class="value">{{flat.data.data.flat_area_sleeping_niche}} m<sup>2</sup></div>
                </div>
                <div v-if="flat.data.data.flat_area_storage" class="d-flex justify-content-between pl-5">
                  <div class="label">{{ $t('Sklad') }}</div>
                  <div class="value">{{flat.data.data.flat_area_storage}} m<sup>2</sup></div>
                </div>
                <div v-if="flat.data.data.flat_area_external_storage1" class="d-flex justify-content-between pl-5">
                  <div class="label">{{ $t('Exteriérový sklad') }}</div>
                  <div class="value">{{flat.data.data.flat_area_external_storage1}} m<sup>2</sup></div>
                </div>
                <div v-if="flat.data.data.flat_area_external_storage2" class="d-flex justify-content-between pl-5">
                  <div class="label">{{ $t('Exteriérový sklad 2') }}</div>
                  <div class="value">{{flat.data.data.flat_area_external_storage2}} m<sup>2</sup></div>
                </div>
                <div v-if="flat.data.data.flat_area_terrace" class="d-flex justify-content-between pl-5">
                  <div class="label">{{ $t('Terasa') }}</div>
                  <div class="value">{{flat.data.data.flat_area_terrace}} m<sup>2</sup></div>
                </div>
                <div v-if="flat.data.data.flat_area_terrace2" class="d-flex justify-content-between pl-5">
                  <div class="label">{{ $t('Terasa 2') }}</div>
                  <div class="value">{{flat.data.data.flat_area_terrace2}} m<sup>2</sup></div>
                </div>
                <div v-if="flat.data.data.flat_area_cloakroom" class="d-flex justify-content-between pl-5">
                  <div class="label">{{ $t('Šatňa') }}</div>
                  <div class="value">{{flat.data.data.flat_area_cloakroom}} m<sup>2</sup></div>
                </div>
                <div v-if="flat.data.data.flat_area_toilette" class="d-flex justify-content-between pl-5">
                  <div class="label">{{ $t('WC') }}</div>
                  <div class="value">{{flat.data.data.flat_area_toilette}} m<sup>2</sup></div>
                </div>
              </div>
            </div>
            <div>
              <div v-if="flat.data.data.flat_area" class="d-flex justify-content-between border-top font-weight-bold mt-3">
                <div class="label">{{ $t('ÚŽITKOVÁ PLOCHA BYTU') }}</div>
                <div class="value">{{flat.data.data.flat_area}} m<sup>2</sup></div>
              </div>
              <div v-if="flat.data.data.flat_area_total" class="d-flex justify-content-between font-weight-bold">
                <div class="label">{{ $t('ÚŽITKOVÁ PLOCHA CELKOVO') }}</div>
                <div class="value">{{flat.data.data.flat_area_total}} m<sup>2</sup></div>
              </div>

              <div v-if="flat.data.data.flat_price_before_discount_vat" class="d-flex justify-content-between font-weight-bold price mt-3">
                <div v-if="flat.data.data.flat_status === '0'" class="label">{{ $t('Cena s DPH') }}:</div>
                <div v-if="isPriceOnRequest(flat.data.data)" class="value">Cena na vyžiadanie</div>
                <div v-else-if="flat.data.data.flat_status === '0'" class="value">{{currency(flat.data.data.flat_price_before_discount_vat)}} €</div>
                <div v-if="flat.data.data.flat_status === '1'">{{ $t('Predrezervované') }}</div>
              </div>
            </div>
          </b-col>
        </div>
        <div class="d-flex justify-content-center mt-3">
          <b-link class="btn btn-primary" target="_blank" :href="linkComparePdf">{{ $t('Stiahnuť PDF') }}</b-link>
        </div>
      </b-modal>
    </div>
    <div class="d-flex spinner" v-if="isLoading">
      <div class="lds-ellipsis"><div></div><div></div><div></div><div></div></div>
    </div>
  </div>
</template>

<script>
import 'vue-slider-component/theme/default.css'
import FlatsFilter from '@/components/flats/FlatsFilter.vue'
import BenefitIcon from '@/components/shared/BenefitIcon'
import { mapGetters, mapActions } from 'vuex'
import flatsCompareMixin from '@/mixins/flatsCompareMixin'

export default {
  name: "FlatsTable",
  mixins: [flatsCompareMixin],
  components: {
    FlatsFilter,
    BenefitIcon
  },

  computed: {
    ...mapGetters('flats', ['flats', 'filteredFlats']),

    // ikonky z etáp tejto tabuľky, bez duplicít
    legendHints() {
      const hints = this.stages.flatMap(stage => this.priceHints[stage] || [])
      return hints.filter((hint, i) => hints.findIndex(h => h.icon === hint.icon) === i)
    }
  },
  props: {
    stages: {
      required: false,
      default: () => []
    },
    showStageAndDate: {
      type: Boolean,
      required: false,
      default: true
    },
    showEntrance: {
      type: Boolean,
      required: false,
      default: false
    },
    showDate: {
      type: Boolean,
      required: false,
      default: false
    },
    showVr: {
      type: Boolean,
      required: false,
      default: false
    },
    showPrice: {
      type: Boolean,
      required: false,
      default: true
    },
    showMonthly: {
      type: Boolean,
      required: false,
      default: true
    },
    hideStatus: {
      type: Boolean,
      required: false,
      default: false
    },
    // legenda ikoniek pri cene (etapy z priceHints)
    showIconLegend: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      // ikonky pri cene podľa etapy, vysvetlenie na hover
      priceHints: {
        boryNoveBloky: [
          { icon: 'inflacia', label: 'Bez inflačnej doložky' },
          { icon: 'parking', label: 'Parking s opciou kúpy' },
        ],
        bory5: [
          { icon: 'inflacia', label: 'Bez inflačnej doložky' },
        ],
      },
      fields: [
        {
          key: 'building.building_stage',
          label: this.$t('Etapa'),
          sortable: true
        },
        {key: 'flat_number_of_rooms', label: this.$t('Počet izieb'), sortable: true},
        {
          key: this.showPrice ? 'flat_price_before_discount_vat' : '',
          label: this.$t('Cena s DPH'),
          sortable: true,
          thStyle: 'min-width: 125px',
          formatter: (value, key, item) => {
            if (this.isPriceOnRequest(item)) {
              return 'Cena na vyžiadanie'
            } else {
              let price = Math.round(parseFloat(item.flat_price_before_discount_vat))
              price = price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
              return item.flat_status === '0' ? `${price} €` : ''
            }
          }
        },
        {
          key: this.showMonthly ? 'flat_monthly_payment' : '',
          label: 'Indikatívna výška mesačnej splátky',
          sortable: true,
          thStyle: 'min-width: 100px',
          formatter: (value, key, item) => {
            let price = Math.round(parseFloat(item.flat_monthly_payment))
price = price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
return item.flat_monthly_payment ? `${price} €` : ''
          }
        },
        {
          key: 'building.stage_completion_date',
          label: this.$t('Stav'),
          sortable: true,
          sortByFormatted: true,
          formatter: (value, key, item) => {
            if (item.building.building_stage == 'bory5') {
              return this.$t('Vo výstavbe')
            } else if (item.building.building_stage == 'boryNoveBloky') {
              return this.$t('V príprave')
            } else if (item.flat_status == 0 && item.building.building_stage !== 'bory4') {
              return this.$t('K nasťahovaniu ihneď')
              // return value.replaceAll('.', '. ')
            } else if (item.building.building_stage == 'bory4') {
              // Realpad stav etapy neposiela; klient chce "Skolaudované" len v tabuľke na stránke Na Hrádzi
              return this.stages.includes('bory4') ? this.$t('Skolaudované') : '-'
            } else {
              return ''
            }
          }
        },
        {
          key: 'building.building_name',
          label: this.$t('Budova'),
          sortable: true
        },
        {
          key: 'flat_entrance',
          label: this.$t('Vchod'),
          sortable: true
        },
        {
          key: 'flat_type',
          label: this.$t('Typ'),
          sortable: true,
          thStyle: 'text-align: center'
        },
        {
          key: 'flat_penta_internal_id',
          label: this.$t('Číslo bytu'),
          sortable: true
        },
        {key: 'building.floor_number', label: this.$t('Podlažie'), sortable: true},
        {
          key: 'flat_area',
          label: this.$t('Výmera bytu'),
          sortable: true,
          formatter: (value, key, item) => {
            if (value)
              return value.replace('.', ',')
            else
              return '0'
          }
        },
        {
          key: 'flat_area_other',
          label: this.$t('Loggia / terasa / záhrada'),
          sortable: true,
          formatter: (value, key, item) => {
            if (value)
              return value.toString().replace('.', ',')
            else
              return '0'
          }
        },
        {
          key: 'flat_area_total',
          label: this.$t('Spolu'),
          sortable: true,
          formatter: (value, key, item) => {
            if (item.building.building_stage == 'bory5') {
              const area = (Number(item.flat_area_total) + Number(item.flat_area_other)).toFixed(2)
              return area.replace('.', ',')
            } else if (item.building.building_stage == 'boryNoveBloky') {
              return item.flat_area_flat_and_exterior
            } else if (value)
              return value.replace('.', ',')
            else
              return '0'
          }
        },
        {key: 'flat_status', label: this.$t('Dostupnosť'), sortable: true},
        {key: this.showVr ? 'url_link_to_the_vr_apartments_tour' : '', label: 'VR', class: 'text-center'},
        {key: 'porovnat', label: this.$t('Porovnať byty')},
      ].filter(f => !this.hideStatus || f.key !== 'flat_status')
    }
  },

  methods: {
    ...mapActions('flats', ['loadFlats']),

    formatMonthlyPayment(value) {
      let price = Math.round(parseFloat(value))
      price = price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
      return `${price} €`
    }
  }
}
</script>

<style lang="sass" scoped>
$md: 768px

.premium-standard
  position: relative

  &:hover
    .popup-info
      width: 240px
      transition: all 0.3s ease

      .text
        opacity: 1
        transition: all 0.6s 0.2s ease
  .icon
    width: 36px
    height: 36px
    display: flex
    align-items: center
    justify-content: center

    img
      max-width: 100%

  .popup-info
    content: ""
    position: absolute
    z-index: 9
    top: 50%
    left: 40px
    width: 0px
    height: 60px
    transform: translateY(-50%)
    background: var(--blue)
    transition: all 0.3s 0.3s ease
    display: flex
    align-items: center

    .text
      font-size: 16px
      font-weight: 600
      color: var(--dark)
      text-align: center
      line-height: 1
      opacity: 0
      width: 240px
      transition: all 0.6s ease
      pointer-events: none


.payment-info-wrap
  position: relative
  display: inline-flex
  align-items: center
  cursor: pointer

  .info-icon
    color: #888
    display: block

  &:hover
    .payment-tooltip
      opacity: 1
      visibility: visible
      transform: translateY(-4px)

  .payment-tooltip
    position: absolute
    bottom: calc(100% + 8px)
    left: 50%
    transform: translateX(-50%) translateY(0)
    width: 280px
    background: #fff
    border: 1px solid #ddd
    border-radius: 4px
    box-shadow: 0 2px 8px rgba(0,0,0,0.15)
    padding: 10px 12px
    opacity: 0
    visibility: hidden
    transition: opacity 0.2s ease, transform 0.2s ease
    pointer-events: none
    z-index: 100

    &::before
      content: ""
      position: absolute
      top: 100%
      left: 50%
      transform: translateX(-50%)
      border: 7px solid transparent
      border-top-color: #ddd

    &::after
      content: ""
      position: absolute
      top: 100%
      left: 50%
      transform: translateX(-50%)
      margin-top: -1px
      border: 6px solid transparent
      border-top-color: #fff

    .text
      font-size: 12px
      line-height: 1.5
      color: #333
      font-weight: 400


.price-bubble
  background: var(--primary)
  width: 60px
  height: 60px
  display: inline-block
  border-radius: 50%
  color: #fff
  padding: 5px
  display: flex
  flex: none
  align-items: center
  margin-left: 20px
  font-size: 11px

.price-hint-legend
  color: var(--primary)
  font-size: 13px

  .icon
    width: 22px
    height: 22px
    margin-right: 6px
    flex: none

// ikonka pri cene, vysvetlenie na hover (rovnaký vzhľad ako payment-tooltip)
.price-hint
  position: relative
  display: inline-flex
  color: var(--primary)
  cursor: pointer

  .icon
    width: 22px
    height: 22px

  &:hover
    .price-hint-tooltip
      opacity: 1
      visibility: visible
      transform: translateX(-50%) translateY(-4px)

  .price-hint-tooltip
    position: absolute
    bottom: calc(100% + 8px)
    left: 50%
    transform: translateX(-50%) translateY(0)
    white-space: nowrap
    background: #fff
    border: 1px solid #ddd
    border-radius: 4px
    box-shadow: 0 2px 8px rgba(0,0,0,0.15)
    padding: 6px 10px
    font-size: 12px
    line-height: 1.5
    color: #333
    opacity: 0
    visibility: hidden
    transition: opacity 0.2s ease, transform 0.2s ease
    pointer-events: none
    z-index: 100

    &::before
      content: ""
      position: absolute
      top: 100%
      left: 50%
      transform: translateX(-50%)
      border: 7px solid transparent
      border-top-color: #ddd

    &::after
      content: ""
      position: absolute
      top: 100%
      left: 50%
      transform: translateX(-50%)
      margin-top: -1px
      border: 6px solid transparent
      border-top-color: #fff

.table-price
  color: var(--primary)

  .text-small
    font-size: 12px

.vr-icon
  color: #001F33
  font-size: 12px
  display: block
.compare-button
  background: #DCF2FF
  box-shadow: 0px 20px 60px rgba(0, 0, 0, 0.2)
  position: fixed
  right: 60px
  bottom: 80px
  z-index: 999
  cursor: pointer
  display: flex
  flex-direction: column
  align-items: center
  justify-content: center
  padding: 15px 25px
  color: #001F33

  @media (max-width: $md)
    right: auto
    left: 0
    bottom: 0
    width: 100%

  .small-text
    font-size: 16px

  a.more
    color: #001F33
    padding-right: 20px
    position: relative

    &:after
      content: ""
      width: 10px
      height: 14px
      background: url('../../assets/icons/arr-dark.svg') no-repeat
      background-size: contain
      position: absolute
      right: 0
      top: 5px

  .compareNum
    background: var(--blue)
    position: absolute
    right: -6px
    top: -6px
    color: #ffffff
    width: 24px
    height: 24px
    border-radius: 50%
    text-align: center
.price
  background: var(--textDark)
  color: #fff
  padding: 5px 10px
  font-size: 20px
.flats-table
  padding: 50px 100px
  position: relative
  max-width: 100%
  // overflow: hidden

  thead
    position: sticky
  @media (max-width: $md)
    padding: 15px
.compare-flats-detail
  font-size: 14px

.remove-from-compare
  @media (max-width: $md)
    position: absolute
    right: 0
    top: 5px
</style>
